import { createContext, useEffect, type ReactNode, useState, useCallback } from "react";

import { getContrastColor, hexToHslTuple, hslToHex } from "./helpers";
import { useThemeStore } from "./themeStore";

type ThemeContextType = {
  primaryColor: string;
  contrastColor: string;
  setColorSchema: (primaryColor: string, contrastColor: string) => void;
};

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useThemeStore((state) => state.theme);

  const [primaryColor, setPrimaryColor] = useState("");
  const [contrastColor, setContrastColor] = useState("");

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    setPrimaryColor(getComputedStyle(root).getPropertyValue("--primary-700").trim());
    setContrastColor(getComputedStyle(root).getPropertyValue("--color-white").trim());
  }, []);

  const handleSetColorSchema = useCallback((primaryColor: string, contrastColor: string) => {
    setPrimaryColor(primaryColor);
    setContrastColor(contrastColor);

    const [h, s, l] = hexToHslTuple(primaryColor);

    const shades = {
      50: 97,
      100: 90,
      200: 80,
      300: 70,
      400: 60,
      500: 50,
      600: 40,
      700: l, // selected color
      800: 25,
      900: 15,
      950: 10,
    };

    Object.entries(shades).forEach(([key, lightness]) => {
      const hexShade = hslToHex(h, s, lightness);

      document.documentElement.style.setProperty(`--primary-${key}`, hexShade);
    });

    document.documentElement.style.setProperty(`--contrast`, contrastColor);
    document.documentElement.style.setProperty(
      `--color-contrast`,
      getComputedStyle(document.documentElement)
        .getPropertyValue(getContrastColor(primaryColor, "--primary-800", "--primary-100"))
        .trim(),
    );
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (typeof window.matchMedia !== "function") {
      document.documentElement.classList.toggle("dark", theme === "dark");
      document.documentElement.style.colorScheme = theme === "dark" ? "dark" : "light";
      return;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = () => {
      const useDark = theme === "dark" || (theme === "system" && media.matches);
      document.documentElement.classList.toggle("dark", useDark);
      document.documentElement.style.colorScheme = useDark ? "dark" : "light";
    };

    applyTheme();
    if (theme !== "system") return;

    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ primaryColor, contrastColor, setColorSchema: handleSetColorSchema }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
